import { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../config/api";
import API_CONFIG from "../config/api";
import chatHubService from "../services/signalR/chatHubService";

/**
 * Custom hook for managing incident chat with SignalR
 * Supports real-time user-to-user chat within an incident
 * @param {object} incident - The incident object
 * @returns {object} Chat state and methods
 */
export default function useIncidentChat(incident) {
  const [messages, setMessages] = useState([]);
  const [connectionState, setConnectionState] = useState("disconnected");
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const incidentIdRef = useRef(incident?.id);
  const historyLoadedRef = useRef(false);

  // Load message history from database
  useEffect(() => {
    if (!incident?.id) return;

    // Prevent loading history multiple times for the same incident
    if (historyLoadedRef.current && incidentIdRef.current === incident.id) {
      return;
    }

    incidentIdRef.current = incident.id;
    historyLoadedRef.current = false;

    const loadMessageHistory = async () => {
      setIsLoadingHistory(true);
      try {
        const response = await apiClient.get(
          API_CONFIG.ENDPOINTS.CHAT_MESSAGES(incident.id)
        );

        if (response.data && response.data.length > 0) {
          // Transform API messages to our format
          const historyMessages = response.data.map((msg) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp || msg.createdAt,
            userId: msg.userId,
            userName: msg.userName,
            userImageUrl: msg.userImageUrl,
          }));
          setMessages(historyMessages);
          historyLoadedRef.current = true;
        } else {
          // No history - start with empty messages
          setMessages([]);
          historyLoadedRef.current = true;
        }
      } catch (err) {
        console.error("Failed to load chat history:", err);
        // Start with empty messages on error (history endpoint might not exist yet)
        setMessages([]);
        historyLoadedRef.current = true;
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadMessageHistory();
  }, [incident?.id]);

  // Connect to SignalR when incident changes
  useEffect(() => {
    if (!incident?.id) return;

    const connect = async () => {
      try {
        setError(null);
        await chatHubService.startConnection(incident.id);
      } catch (err) {
        console.error("Failed to connect to chat:", err);
        setError("Failed to connect to chat service.");
      }
    };

    connect();

    // Cleanup on unmount or incident change
    return () => {
      chatHubService.stopConnection();
    };
  }, [incident?.id]);

  // Subscribe to connection state changes
  useEffect(() => {
    const unsubscribe = chatHubService.onConnectionStateChange((state) => {
      setConnectionState(state);
    });

    return unsubscribe;
  }, []);

  // Subscribe to incoming messages from OTHER users (real-time)
  useEffect(() => {
    const unsubscribe = chatHubService.onMessage((message) => {
      // Transform server message to our format
      const chatMessage = {
        id: message.id || `msg-${Date.now()}`,
        role: message.role || "user",
        content: message.content,
        timestamp: message.timestamp || new Date().toISOString(),
        userId: message.userId,
        userName: message.userName,
        userImageUrl: message.userImageUrl,
      };

      // Add the message from other users
      // Check if this message already exists to avoid duplicates
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === chatMessage.id);
        if (exists) {
          return prev;
        }
        return [...prev, chatMessage];
      });
    });

    return unsubscribe;
  }, []);

  // Subscribe to errors
  useEffect(() => {
    const unsubscribe = chatHubService.onError((err) => {
      console.error("Chat error:", err);
      setError(err.message || "An error occurred");
    });

    return unsubscribe;
  }, []);

  /**
   * Send a message
   * @param {string} content - Message content
   */
  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim()) return;

      const messageContent = content.trim();

      // Check if connected
      if (!chatHubService.isConnected()) {
        setError("Not connected to chat. Please wait or retry connection.");
        return;
      }

      setIsSending(true);

      try {
        // Send via SignalR - server will save and broadcast to all users
        await chatHubService.sendMessage(messageContent, {
          incidentTitle: incident?.title,
          incidentSeverity: incident?.severity,
          incidentStatus: incident?.status,
        });

        // Message will appear via the onMessage callback when server broadcasts it back
        // Or we can add it locally for instant feedback (optimistic update)
        // For now, let's add it locally for instant feedback
        const userMessage = {
          id: `local-${Date.now()}`,
          role: "user",
          content: messageContent,
          timestamp: new Date().toISOString(),
          isLocal: true, // Mark as locally added
        };
        setMessages((prev) => [...prev, userMessage]);

        setError(null);
      } catch (err) {
        console.error("Failed to send message:", err);
        setError("Failed to send message. Please try again.");
      } finally {
        setIsSending(false);
      }
    },
    [incident]
  );

  /**
   * Clear chat history (local only - doesn't delete from database)
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  /**
   * Reload message history from database
   */
  const reloadHistory = useCallback(async () => {
    if (!incident?.id) return;

    setIsLoadingHistory(true);
    try {
      const response = await apiClient.get(
        API_CONFIG.ENDPOINTS.CHAT_MESSAGES(incident.id)
      );

      if (response.data && response.data.length > 0) {
        const historyMessages = response.data.map((msg) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp || msg.createdAt,
          userId: msg.userId,
          userName: msg.userName,
          userImageUrl: msg.userImageUrl,
        }));
        setMessages(historyMessages);
      } else {
        setMessages([]);
      }
    } catch (err) {
      console.error("Failed to reload chat history:", err);
      setError("Failed to reload chat history.");
    } finally {
      setIsLoadingHistory(false);
    }
  }, [incident?.id]);

  /**
   * Retry connection
   */
  const retryConnection = useCallback(async () => {
    if (incident?.id) {
      setError(null);
      try {
        await chatHubService.startConnection(incident.id);
      } catch (err) {
        setError("Failed to reconnect. Please try again.");
      }
    }
  }, [incident?.id]);

  return {
    messages,
    connectionState,
    error,
    isSending,
    isLoadingHistory,
    sendMessage,
    clearMessages,
    reloadHistory,
    retryConnection,
    isConnected: connectionState === "connected",
  };
}
